import { GradientCard } from "@/containers/GradientCard";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";

interface BaseModalV2Props {
  /** Modal title - displayed prominently at the top */
  title: string;
  /** Content to render in the middle section */
  children?: React.ReactNode;
  /** Primary action button configuration */
  primaryAction: {
    label: string;
    onClick: () => void;
  };
  /** Optional secondary/cancel action */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Called when clicking outside or pressing escape */
  onClose: () => void;
  /** Optional image URL for the right side panel */
  imageUrl?: string;
  /** Alt text for the image */
  imageAlt?: string;
  /** Optional className for the modal container */
  className?: string;
  /** Optional className for the content section */
  contentClassName?: string;
  /** Whether to show the image on mobile (default: false) */
  showImageOnMobile?: boolean;
  /** Whether to show the modal on small desktop (default: false) */
  smallDesktop?: boolean;
}

export const BaseModalV2 = ({
  title,
  children,
  primaryAction,
  secondaryAction,
  onClose,
  imageUrl,
  imageAlt,
  className,
  contentClassName,
  showImageOnMobile = false,
}: BaseModalV2Props) => {
  const { t } = useTranslation();
  useScrollLock(true);

  const ref = useOutsideClick(() => {
    onClose();
  });

  // z-100 on root: stacks above StickySidebar and other z-50 fixed panels (same z-index loses to later DOM).
  return (
    <div className="fixed inset-0 bg-black/60 z-100 flex items-start justify-center mobile:pt-8 px-4 pb-4 transition-opacity duration-200 ease-out opacity-100 starting:opacity-0">
      <div
        ref={ref}
        className={cn(
          "bg-panel-background border border-panel-foreground rounded-[20px] w-full touch-device:max-h-[460px] large:desktop:max-h-none max-w-[880px] flex overflow-hidden shadow-soft-dark-bottom",
          "transition-all duration-200 ease-out opacity-100 scale-100 starting:opacity-0 starting:scale-95",
          className,
        )}
      >
        {/* Left Content */}
        <div
          className={cn(
            "flex flex-col gap-6 p-8 touch-device:px-12 touch-device:py-8 flex-1",
            contentClassName,
          )}
        >
          {/* Title */}
          <h2 className="font-oswald font-medium text-xl touch-device:text-2xl  text-white tracking-tight leading-tight">
            {title}
          </h2>

          {/* Children Content */}
          {children && <div className="flex flex-col">{children}</div>}

          {/* Actions */}
          <div className="flex flex-col gap-3 touch-device:gap-5 items-center">
            <button
              onClick={primaryAction.onClick}
              className="w-full h-9 bg-primary-action-default hover:bg-primary-action-hover rounded px-2 py-1 flex items-center justify-center shadow-soft-dark-bottom transition-colors"
            >
              <span className="font-oswald font-medium text-base touch-device:text-lg text-white">
                {primaryAction.label}
              </span>
            </button>

            {secondaryAction && (
              <button
                onClick={secondaryAction.onClick}
                className="font-oswald text-sm touch-device:text-base text-dark-tertiary hover:text-dark-secondary transition-colors"
              >
                {secondaryAction.label}
              </button>
            )}
          </div>
        </div>

        {/* Right Side Image */}
        {imageUrl && (
          <img
            className={cn(
              "w-[430px] object-cover",
              !showImageOnMobile && "hidden touch-device:block",
            )}
            src={imageUrl}
            alt={imageAlt ?? t("dialog.imageAlt")}
          />
        )}
      </div>
    </div>
  );
};

/** Reusable info card component for use inside BaseModalV2 */
interface InfoCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  condensed?: boolean;
}

export const ModalInfoCard = ({
  icon,
  title,
  description,
  condensed = false,
}: InfoCardProps) => {
  const Content = () => (
    <div className="flex flex-col gap-1 large-desktop:gap-3">
      <div className="flex items-center gap-2 large-desktop:gap-3">
        {icon}
        <span className="font-oswald font-medium text-sm touch-device:text-base large-desktop:text-lg text-dark-tertiary tracking-tight">
          {title}
        </span>
      </div>
      <p className="text-sm touch-device:text-base text-dark-secondary ">
        {description}
      </p>
    </div>
  );

  if (condensed) {
    return <Content />;
  }

  return (
    <GradientCard padding="p-4">
      <Content />
    </GradientCard>
  );
};
